import { has } from 'es-toolkit/compat';
import { MouseEvent } from 'react';
import { logError, logWarning } from 'shared/helpers/Logger';
import { tryParseJSON } from 'shared/utils/DataUtils';

function debug(format: string, ...args: unknown[]): void {
  if (import.meta.env.MODE !== 'production') {
    // eslint-disable-next-line no-console
    console.debug(`MobileAppBridge: ${format}`, ...args);
  }
}

function warning(error: unknown) {
  if (import.meta.env.MODE !== 'production') {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

type MobileFeatureToggles = Record<
  'isWebViewDynamicNavigationEnabled',
  boolean
>;

export type TokenType = 'DRIVER' | 'CARRIER';

export interface Auth2Tokens {
  DRIVER: string;
  CARRIER: string;
}

// Used both in Webkit and Android interfaces
export interface BaseMobileAppInterface {
  getToken?: () => string | undefined;
  getAuth2Token?: () => string | undefined;
  getDeviceGuid?: () => string | undefined;
  getDriverGuid?: () => string | undefined;
  getCarrierGuid?: () => string | undefined;
  isOnboardingEnabled?: () => boolean;
  isDarkMode?: () => boolean;
  getFeatureToggles?: () => MobileFeatureToggles | string; // String is in JSON format
  getAppVersion?: () => string | undefined;
}

export interface AndroidMobileAppInterface extends BaseMobileAppInterface {
  openProfile?: () => void;
  openSettings?: () => void;
  openDriverInformation?: () => void;
  openDriverPreferences?: () => void;
  openCarrierInformation?: () => void;
  openExternalUrl?: (url: string) => void;
  openACHPaymentInformation?: () => void;
  openAvailableLoads?: () => void;
  openVerifiedCarrierPromotionalModal?: () => void;
  openUnverifiedCarrierRequestWallModal?: () => void;
  refreshAuth2Token?: (token_type: string) => void;
  sendDriverUpdatedEvent?: () => void;
  sendDriverSubmittedFeedbackEvent?: () => void;
  sentSuperPayRequestShipper?: () => void;
  successAndClose?: (message: string) => void;
  closeScreen?: () => void;
  updateSuggestedLoads?: (unseenCount: number) => void;
  acceptedShipperLimitedExperience?: (doNotWarnAgain: boolean) => void;
  openFuelFinder?: () => void;
  openSupport?: () => void;
  refreshSuperCardTabs?: () => void;
}

declare global {
  interface Window {
    MobileLoadboardInterface?:
      | AndroidMobileAppInterface
      | BaseMobileAppInterface;
    webkit?: { messageHandlers?: { slbMessageHandler?: WebkitMessageHandler } };
  }
}

export type WebkitMessageHandlerPayload =
  | {
      type:
        | 'open_profile'
        | 'open_settings'
        | 'open_driver_preferences'
        | 'open_driver_information'
        | 'open_carrier_information'
        | 'open_ach_payment_information'
        | 'open_verified_carrier_promotional_modal'
        | 'open_unverified_carrier_request_wall_modal'
        | 'send_driver_updated_event'
        | 'send_driver_submitted_feedback_event'
        | 'sent_superpay_request_shipper'
        | 'close_screen'
        | 'open_available_loads'
        | 'open_fuel_finder'
        | 'open_support'
        | 'refresh_super_card_tabs';
    }
  | { type: 'open_external_url'; data: { url: string } }
  | { type: 'refresh_auth2_token'; data: { token_type: string } }
  | { type: 'update_suggested_loads'; data: { unseen_count: number } }
  | { type: 'success_and_close'; data: { message: string } }
  | {
      type: 'accepted_shipper_limited_experience';
      data: { do_not_warn_again: boolean };
    };

interface WebkitMessageHandler {
  postMessage: (payload: WebkitMessageHandlerPayload) => void;
}

function execute(
  bridgeMethod: (bridge: AndroidMobileAppInterface) => void,
  messageHandlerMethod: (messageHandler: WebkitMessageHandler) => void,
  fallbackMethod: () => void,
): boolean {
  const messageHandler = window.webkit?.messageHandlers?.slbMessageHandler;

  // Try WebKit handler first and do not return false on error.
  // We want to try call bridge event after all.
  if (messageHandler) {
    try {
      messageHandlerMethod(messageHandler);

      return true;
    } catch (error: unknown) {
      warning(error);
      logError(error, 'MobileAppInterface#messageHandlerMethod');
    }
  }

  const mobileInterface = getMobileLoadboardInterface();

  if (mobileInterface) {
    try {
      bridgeMethod(mobileInterface);

      return true;
    } catch (error: unknown) {
      warning(error);
      logError(error, 'MobileAppInterface#bridgeMethod');

      return false;
    }
  }

  if (import.meta.env.MODE !== 'production') {
    fallbackMethod();
  }

  return false;
}

function executeBaseMethod<T>(
  fn: (base: BaseMobileAppInterface) => T,
): T | undefined {
  const mobileInterface = getMobileLoadboardInterface();

  if (mobileInterface) {
    try {
      return fn(mobileInterface);
    } catch (error: unknown) {
      warning(error);
      logError(error, 'MobileAppInterface#bridgeMethod');

      return undefined;
    }
  }

  return undefined;
}

function getMobileLoadboardInterface():
  | AndroidMobileAppInterface
  | BaseMobileAppInterface
  | undefined {
  try {
    return window.MobileLoadboardInterface;
  } catch (error: unknown) {
    logError(error, 'MobileAppInterface#getMobileLoadboardInterface');
    return undefined;
  }
}

export function getMobileAppToken(): string | undefined {
  return executeBaseMethod((bridge) => bridge.getToken?.());
}

export const MIN_APP_VERSION_FOR_AUTH2 = '3.1.10';

export function compareVersions(version1: string, version2: string): number {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }

  return 0;
}

function isAppVersionSupportsAuth2(): boolean {
  const appVersion = executeBaseMethod((bridge) => bridge.getAppVersion?.());

  if (!appVersion) return false;

  return compareVersions(appVersion, MIN_APP_VERSION_FOR_AUTH2) >= 0;
}

export function getMobileAppAuth2Token(): string | undefined {
  if (!isAppVersionSupportsAuth2()) return undefined;

  const tokenJson = executeBaseMethod((bridge) => bridge.getAuth2Token?.());

  if (!tokenJson) return undefined;

  const tokens = tryParseJSON<Auth2Tokens>(tokenJson);
  if (!tokens) return undefined;

  return tokens.CARRIER || tokens.DRIVER;
}

export class MobileAppBridge {
  static isInjected(): boolean {
    return getMobileLoadboardInterface() != null;
  }

  static getDriverGuid(): string | undefined {
    return executeBaseMethod((bridge) => bridge.getDriverGuid?.());
  }

  static getDeviceGuid(): string | undefined {
    return executeBaseMethod((bridge) => bridge.getDeviceGuid?.());
  }

  static getCarrierGuid(): string | undefined {
    return executeBaseMethod((bridge) => bridge.getCarrierGuid?.());
  }

  static getAppVersion(): string | undefined {
    return executeBaseMethod((bridge) => bridge.getAppVersion?.());
  }

  static isOnboardingEnabled = (): boolean => {
    return (
      executeBaseMethod((bridge) => bridge.isOnboardingEnabled?.()) ?? false
    );
  };

  static getFeatureToggles(): MobileFeatureToggles | undefined {
    const value = executeBaseMethod((bridge) => bridge.getFeatureToggles?.());

    if (typeof value === 'string') {
      return tryParseJSON<MobileFeatureToggles>(value);
    }

    return value;
  }

  static openProfile = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openProfile?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_profile' });
      },
      () => {
        debug('openProfile');
      },
    );

  static openFuelFinder = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openFuelFinder?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_fuel_finder' });
      },
      () => {
        debug('openFuelFinder');
      },
    );

  static refreshSuperCardTabs = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.refreshSuperCardTabs?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'refresh_super_card_tabs' });
      },
      () => {
        debug('refreshSuperCardTabs');
      },
    );

  static openSupport = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openSupport?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_support' });
      },
      () => {
        debug('openSupport');
      },
    );

  static openSettings = (): boolean =>
    execute(
      (mobileInterface) => {
        if (mobileInterface.openSettings) {
          mobileInterface.openSettings();
        } else {
          MobileAppBridge.openProfile();
        }
      },
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_settings' });
      },
      () => {
        debug('openSettings');
      },
    );

  static openDriverInformation = (): boolean =>
    execute(
      (mobileInterface) => {
        if (typeof mobileInterface.openDriverInformation == 'function') {
          mobileInterface.openDriverInformation();
        } else {
          MobileAppBridge.openProfile();
        }
      },
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_driver_information' });
      },
      () => {
        debug('openDriverInformation');
      },
    );

  static openCarrierInformation = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openCarrierInformation?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_carrier_information' });
      },
      () => {
        debug('openCarrierInformation');
      },
    );

  static openExternalUrl = (url: string): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openExternalUrl?.(url),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'open_external_url',
          data: { url },
        });
      },
      () => {
        debug('openExternalUrl');
      },
    );

  static openAvailableLoads = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openAvailableLoads?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_available_loads' });
      },
      () => {
        debug('openAvailableLoads');
      },
    );

  static openACHPaymentInformation = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openACHPaymentInformation?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'open_ach_payment_information',
        });
      },
      () => {
        debug('openACHPaymentInformation');
      },
    );

  static openVerifiedCarrierPromotionalModal = (): boolean =>
    execute(
      (mobileInterface) =>
        mobileInterface.openVerifiedCarrierPromotionalModal?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'open_verified_carrier_promotional_modal',
        });
      },
      () => {
        debug('openVerifiedCarrierPromotionalModal');
      },
    );

  static openUnverifiedCarrierRequestWallModal = (): boolean =>
    execute(
      (mobileInterface) =>
        mobileInterface.openUnverifiedCarrierRequestWallModal?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'open_unverified_carrier_request_wall_modal',
        });
      },
      () => {
        debug('openUnverifiedCarrierRequestWallModal');
      },
    );

  static refreshAuth2Token = (tokenType: TokenType): boolean =>
    execute(
      (mobileInterface) => mobileInterface.refreshAuth2Token?.(tokenType),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'refresh_auth2_token',
          data: { token_type: tokenType },
        });
      },
      () => {
        debug('refreshAuth2Token');
      },
    );

  static sendDriverUpdatedEvent = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.sendDriverUpdatedEvent?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'send_driver_updated_event' });
      },
      () => {
        debug('sendDriverUpdatedEvent');
      },
    );

  static sendDriverSubmittedFeedbackEvent = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.sendDriverSubmittedFeedbackEvent?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'send_driver_submitted_feedback_event',
        });
      },
      () => {
        debug('sendDriverSubmittedFeedbackEvent');
      },
    );

  static interceptLinkClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (MobileAppBridge.openExternalUrl(event.currentTarget.href)) {
      event.preventDefault();
    }
  };

  static updateSuggestedLoads = (data: { unseen_count: number }): boolean =>
    execute(
      (mobileInterface) =>
        mobileInterface.updateSuggestedLoads?.(data.unseen_count),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'update_suggested_loads',
          data,
        });
      },
      () => {
        debug('updateSuggestedLoads');
      },
    );

  static openDriverPreferences = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.openDriverPreferences?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'open_driver_preferences' });
      },
      () => {
        debug('openDriverPreferences');
      },
    );

  static sentSuperPayRequestShipper = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.sentSuperPayRequestShipper?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'sent_superpay_request_shipper',
        });
      },
      () => {
        debug('sentSuperPayRequestShipper');
      },
    );

  static acceptShipperLimitedExperience = (doNotWarn: boolean): boolean =>
    execute(
      (mobileInterface) =>
        mobileInterface.acceptedShipperLimitedExperience?.(doNotWarn),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'accepted_shipper_limited_experience',
          data: { do_not_warn_again: doNotWarn },
        });
      },
      () => {
        debug('acceptShipperLimitedExperience');
      },
    );

  static successAndClose = (message: string): boolean =>
    execute(
      (mobileInterface) => mobileInterface.successAndClose?.(message),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({
          type: 'success_and_close',
          data: { message },
        });
      },
      () => {
        debug('successAndClose', message);
      },
    );

  static closeScreen = (): boolean =>
    execute(
      (mobileInterface) => mobileInterface.closeScreen?.(),
      (webkitMessageHandler) => {
        webkitMessageHandler.postMessage({ type: 'close_screen' });
      },
      () => {
        debug('closeScreen');
      },
    );

  static hasMethodSupported = (
    method: keyof AndroidMobileAppInterface,
  ): boolean => has(getMobileLoadboardInterface(), method);

  static isDarkMode = (): boolean => {
    return executeBaseMethod((bridge) => bridge.isDarkMode?.()) ?? false;
  };

  static listenDarkModeChanges = (callback: (isDarkMode: boolean) => void) => {
    function handleDarkModeChange(event: CustomEvent<{ isDarkMode: boolean }>) {
      callback(event.detail.isDarkMode);
    }

    window.addEventListener(
      'driver_app_theme_mode_changed',
      handleDarkModeChange,
    );

    return () => {
      window.removeEventListener(
        'driver_app_theme_mode_changed',
        handleDarkModeChange,
      );
    };
  };

  static listenAuth2TokenUpdates = (
    callback: (tokens: Auth2Tokens) => void,
  ) => {
    function handleTokenUpdate(event: CustomEvent<Auth2Tokens>) {
      callback(event.detail);
    }

    function handleTokenError(event: CustomEvent) {
      warning('Auth2 token refresh failed');
      debug('Auth2 token refresh error', event.detail);
    }

    window.addEventListener(
      'driver_app_auth2_token_updated',
      handleTokenUpdate,
    );

    window.addEventListener(
      'driver_app_auth2_token_refresh_error',
      handleTokenError,
    );

    return () => {
      window.removeEventListener(
        'driver_app_auth2_token_updated',
        handleTokenUpdate,
      );
      window.removeEventListener(
        'driver_app_auth2_token_refresh_error',
        handleTokenError,
      );
    };
  };

  static refreshAuth2TokenAsync = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      function handleSuccess() {
        cleanup();
        logWarning('Auth token refreshed after 401 error');
        resolve(true);
      }

      function handleError() {
        cleanup();
        logWarning('Failed to refresh auth token after 401 error');
        resolve(false);
      }

      function cleanup() {
        window.removeEventListener(
          'driver_app_auth2_token_updated',
          handleSuccess,
        );
        window.removeEventListener(
          'driver_app_auth2_token_refresh_error',
          handleError,
        );
      }

      window.addEventListener('driver_app_auth2_token_updated', handleSuccess);
      window.addEventListener(
        'driver_app_auth2_token_refresh_error',
        handleError,
      );

      MobileAppBridge.refreshAuth2Token('CARRIER');
    });
  };
}
